import { useState, useEffect } from 'react';

// Module-level cache to avoid repeated requests for the same URL
const cache = new Map();

/**
 * Custom hook that checks if an asset really exists at the given URL via a HEAD
 * request. Results are cached in a module-level Map so the same URL is never
 * fetched twice.
 *
 * IMPORTANTE: em hospedagem SPA (S3 + CloudFront) uma URL inexistente costuma
 * responder 200 com o index.html (fallback de rota). Por isso não basta checar
 * status 200 — validamos também que o Content-Type corresponde ao tipo esperado
 * (ex: "video/" para vídeos, "image/" para screenshots). Sem isso, o hero
 * acharia que o vídeo existe, tentaria tocar o HTML do index como .mp4 e
 * renderizaria um <video> vazio.
 *
 * @param {string} url - The URL to check
 * @param {string} [expectedType] - Prefixo de Content-Type esperado (ex: 'video/').
 *   Default inferido pela extensão da URL.
 * @returns {{ exists: boolean, loading: boolean }}
 */
function inferExpectedType(url) {
  const u = (url || '').toLowerCase();
  if (/\.(mp4|webm|ogg|mov)(\?|$)/.test(u)) return 'video/';
  if (/\.(png|jpe?g|gif|webp|avif|svg)(\?|$)/.test(u)) return 'image/';
  return null;
}

function useAssetDetector(url, expectedType) {
  const [exists, setExists] = useState(() => {
    if (cache.has(url)) {
      return cache.get(url);
    }
    return false;
  });

  const [loading, setLoading] = useState(() => {
    return !cache.has(url);
  });

  useEffect(() => {
    if (cache.has(url)) {
      setExists(cache.get(url));
      setLoading(false);
      return;
    }

    let cancelled = false;
    const typePrefix = expectedType || inferExpectedType(url);

    fetch(url, { method: 'HEAD' })
      .then((response) => {
        const contentType = response.headers.get('content-type') || '';
        // Precisa ser 200 E, quando sabemos o tipo esperado, bater com ele.
        // Isso evita o falso-positivo do fallback SPA (index.html → text/html).
        const result =
          response.status === 200 &&
          (!typePrefix || contentType.toLowerCase().startsWith(typePrefix));
        cache.set(url, result);
        if (!cancelled) {
          setExists(result);
          setLoading(false);
        }
      })
      .catch(() => {
        cache.set(url, false);
        if (!cancelled) {
          setExists(false);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [url, expectedType]);

  return { exists, loading };
}

export default useAssetDetector;
